// Session storage utilities with expiry
const SESSION_EXPIRY_HOURS = 24; // Cart expires after 24 hours

interface StoredCart {
  items: any[];
  timestamp: number;
  sessionId: string;
}

export const sessionStorage = {
  // Save cart with timestamp
  saveCart: (items: any[], sessionId: string) => {
    const cartData: StoredCart = {
      items,
      timestamp: Date.now(),
      sessionId,
    };
    localStorage.setItem('cart', JSON.stringify(cartData));
  },

  // Load cart and check if it's expired
  loadCart: (): StoredCart | null => {
    try {
      const stored = localStorage.getItem('cart');
      if (!stored) return null;

      const cartData: StoredCart = JSON.parse(stored);
      const expiryTime = SESSION_EXPIRY_HOURS * 60 * 60 * 1000;
      const isExpired = Date.now() - cartData.timestamp > expiryTime;

      if (isExpired) {
        // Clear expired cart
        localStorage.removeItem('cart');
        localStorage.removeItem('cart_session_id');
        return null;
      }

      return cartData;
    } catch (error) {
      console.error('Error loading cart from storage:', error);
      return null;
    }
  },

  // Update cart timestamp (extend session)
  updateTimestamp: () => {
    const stored = localStorage.getItem('cart');
    if (stored) {
      const cartData = JSON.parse(stored);
      cartData.timestamp = Date.now();
      localStorage.setItem('cart', JSON.stringify(cartData));
    }
  },

  // Clear all cart data
  clearCart: () => {
    localStorage.removeItem('cart');
    localStorage.removeItem('cart_session_id');
  },

  // Get remaining session time in minutes
  getRemainingTime: (): number | null => {
    const stored = localStorage.getItem('cart');
    if (!stored) return null;

    try {
      const cartData: StoredCart = JSON.parse(stored);
      const expiryTime = SESSION_EXPIRY_HOURS * 60 * 60 * 1000;
      const elapsed = Date.now() - cartData.timestamp;
      const remaining = expiryTime - elapsed;

      return remaining > 0 ? Math.floor(remaining / (1000 * 60)) : 0;
    } catch {
      return null;
    }
  },
};

// Auto-save cart state periodically
export const setupAutoSave = (getItems: () => any[], sessionId: string) => {
  // Save every 30 seconds if there are items
  const interval = setInterval(() => {
    const items = getItems();
    if (items.length > 0) {
      sessionStorage.updateTimestamp();
    }
  }, 30000);

  return () => clearInterval(interval);
};